// React import
import React, { useMemo, useState } from 'react';

// Generic imports
import _ from 'lodash';
import pluralize from 'pluralize';

// Material UI import
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

// FontAwesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare, faSquareMinus } from '@fortawesome/pro-light-svg-icons';

function SidePaneRfqsSupplierSelection(props) {
    const { single, suppliers, selectedSuppliers, setSelectedSuppliers, transportationMethod, selectedRoute, filterByEmail = true } = props;
    const [ autocompleteInput, setAutocompleteInput ] = useState('');
    const [ displayCount, setDisplayCount ] = useState(100);

    const suppliersFilteredByEmail = useMemo(() => {
        return _(suppliers).filter(s => !filterByEmail || s.contacts?.[0]?.email).value();
    }, [suppliers, filterByEmail]);

    const suppliersByName = useMemo(() => {
        return _.keyBy(suppliersFilteredByEmail, s => s.name.toUpperCase());
    }, [suppliersFilteredByEmail]);

    const matchedUnmatched = useMemo(() => {
        const result = _(suppliersFilteredByEmail).map(s => s.name).uniq().map(a => {
            const supplier = suppliersByName[a.toUpperCase()];
            if (!supplier) return [[a, 'Unmatched']];
            if (!selectedRoute) return [[a, 'Unmatched']];
            const routes = supplier.transportationMethods?.[transportationMethod]?.routes || [];
            if (routes.find(r => r.source?.countryCode === selectedRoute.source?.countryCode && r.destination?.countryCode === selectedRoute.destination?.countryCode)) {
                return [
                    [a, 'Matched-By-Route-Source-And-Destination'],
                    ['TITLE-Matched-By-Route-Source-And-Destination', 'Matched-By-Route-Source-And-Destination']
                ];
            }
            if (routes.find(r => r.source?.countryCode === selectedRoute.source?.countryCode)) {
                return [
                    [a, 'Matched-By-Route-Source'],
                    ['TITLE-Matched-By-Route-Source', 'Matched-By-Route-Source']
                ];
            }
            if (routes.find(r => r.destination?.countryCode === selectedRoute.destination?.countryCode)) {
                return [
                    [a, 'Matched-By-Route-Destination'],
                    ['TITLE-Matched-By-Route-Destination', 'Matched-By-Route-Destination']
                ];
            }

            const [routeSourceAddress, routeDestinationAddress, supplierAddress] = [
                [selectedRoute?.source, selectedRoute?.destination, supplier?.address].map(a => {
                    if (a?.countryCode === 'US') {
                        // Try to match the state from the address. It could be either something like 'Huston, TX, USA', or '1723 MainSt, Huston, TX 72114, USA'
                        const state = a?.text?.match(/,\s*([A-Z]{2})(?:\s*\d{5})?,\s*USA$/);
                        
                        if (state) {
                            return `${state[1]}, USA`;
                        }
                        return 'US';
                    }
                    return a?.countryCode;
                })
            ];

            if (supplierAddress && routeSourceAddress && routeDestinationAddress && supplierAddress === routeSourceAddress && supplierAddress === routeDestinationAddress) {
                return [
                    [a, 'Matched-By-Address-Source-And-Destination'],
                    ['TITLE-Matched-By-Address-Source-And-Destination', 'Matched-By-Address-Source-And-Destination']
                ];
            }
            if (supplierAddress && routeSourceAddress && supplierAddress === routeSourceAddress) {
                return [
                    [a, 'Matched-By-Address-Source'],
                    ['TITLE-Matched-By-Address-Source', 'Matched-By-Address-Source']
                ];
            }
            if (supplierAddress && routeDestinationAddress && supplierAddress === routeDestinationAddress) {
                return [
                    [a, 'Matched-By-Address-Destination'],
                    ['TITLE-Matched-By-Address-Destination', 'Matched-By-Address-Destination']
                ];
            }
            return [
                [a, 'Unmatched'],
                ['TITLE-Unmatched', 'Unmatched']
            ];
        }).flatten().value();
        return _(result).uniqWith(_.isEqual).fromPairs().value();
    }, [suppliersFilteredByEmail, transportationMethod, selectedRoute]);

    const supplierNameToSupplier = useMemo(() => {
        return _(suppliersFilteredByEmail).map(s => [s.name.toUpperCase(), s]).fromPairs().value();
    }, [suppliersFilteredByEmail]);

    const sortedSuppliers = useMemo(() => {
        return _(matchedUnmatched).keys().sortBy(a => {
            const matched = {
                'Matched-By-Route-Source-And-Destination': 0,
                'Matched-By-Route-Source': 1,
                'Matched-By-Route-Destination': 2,
                'Matched-By-Address-Source-And-Destination': 3,
                'Matched-By-Address-Source': 4,
                'Matched-By-Address-Destination': 5,
                'Unmatched': 6,
            }[matchedUnmatched[a]];
            if (a.startsWith('TITLE-')) {
                return matched + '_0_' + a.toUpperCase();
            }
            return matched + '_1_' + a.toUpperCase();
        }).value();
    }, [ matchedUnmatched ]);

    const [allTags, tagsToSuppliers] = useMemo(() => {
        const allTags = _(suppliersFilteredByEmail).map(s => s.tags?.map(t => t.label.toUpperCase())).compact().flatten().uniq().value();
        const tagsToSuppliers = _(allTags).map(t => [t, _(suppliersFilteredByEmail).filter(s => s.tags?.some(tt => tt.label.toUpperCase() === t)).map(s => s.name).value()]).fromPairs().value();
        return [allTags, tagsToSuppliers];
    }, [suppliersFilteredByEmail]);

    const visibles = useMemo(() => {
        if (autocompleteInput.startsWith('#')) {
            const filteredTags = allTags.filter(t => t.startsWith(autocompleteInput.substring(1).toUpperCase()));
            const suppliers = [...sortedSuppliers.filter(a => a.startsWith('TITLE-')), ..._(filteredTags).map(t => tagsToSuppliers[t]).flatten().uniq().value()];
            return _.sortBy(suppliers, a => sortedSuppliers.indexOf(a));
        }
        return sortedSuppliers.filter( a => {
            if (a.startsWith('TITLE-')) return true;
            if (autocompleteInput.startsWith('#')) {
                return tagsToSuppliers[autocompleteInput.substring(1).toUpperCase()]?.includes(a);
            }
            if (a.toUpperCase().includes(autocompleteInput.toUpperCase())) return true;
            return false;
        });
    }, [sortedSuppliers, autocompleteInput]);

    const visibleByCategory = useMemo(() => {
        return _(visibles).filter(a => !a.startsWith('TITLE-')).groupBy(a => matchedUnmatched[a]).value(); 
    }, [visibles]);

    return (
        <>
            <Typography variant='body1' sx={{ mb: 1, fontWeight: 800 }}>
                {pluralize('Supplier', single ? 1 : 10)}
            </Typography>
            {(selectedSuppliers.length === 0 || !single) && (
                <>
                    <Typography variant='caption' component='div' sx={{ paddingBottom: '12px' }}>
                        Please select the {pluralize('supplier', single ? 1 : 10)} you want to {single ? 'assign for these rates' : 'send the RFQ to'}.
                    </Typography>
                    <TextField
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px 4px 0px 0px' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        label='Supplier'
                        value={autocompleteInput}
                        autoFocus={true}
                        onChange={e => {
                            setAutocompleteInput(e.target.value);
                            setDisplayCount(100);
                        }}
                        fullWidth
                    />
                    <Box elevation={1} sx={{ padding: '12px', maxHeight: '200px', overflowY: 'auto', borderRadius: '0px 0px 4px 4px', border: '1px solid', borderColor: 'divider', borderTop: 'none' }}
                        onScroll={(e) => {
                            const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
                            if (bottom) {
                                setDisplayCount(prev => Math.min(prev + 100, sortedSuppliers.length));
                            }
                        }}
                    >
                        {visibles.slice(0, displayCount).map(s => (
                            <Option
                                key={s}
                                option={s}
                                single={single}
                                selectedSuppliers={selectedSuppliers}
                                matchedUnmatched={matchedUnmatched}
                                visibleByCategory={visibleByCategory}
                                supplierNameToSupplier={supplierNameToSupplier}
                                onClick={() => {
                                    if (single) {
                                        setSelectedSuppliers([suppliers.find(ss => ss.name.toUpperCase() === s?.toUpperCase())]);
                                    } else if (s.startsWith('TITLE-')) {
                                        const _suppliers = visibleByCategory[matchedUnmatched[s]];
                                        const suppliersToAdd = _suppliers?.filter(s => !s.startsWith('TITLE-')).map(s => suppliers.find(ss => ss.name.toUpperCase() === s.toUpperCase()));

                                        const every = _suppliers?.every(a => selectedSuppliers.find(s => s.name.toUpperCase() === a.toUpperCase()));

                                        if (every) {
                                            setSelectedSuppliers(selectedSuppliers.filter(ss => !_suppliers.find(a => a.toUpperCase() === ss.name.toUpperCase())));
                                        } else {
                                            setSelectedSuppliers(_.uniqBy([
                                                ...selectedSuppliers,
                                                ...suppliersToAdd
                                            ], 'name'));
                                        }
                                    } else {
                                        const isSelected = selectedSuppliers.find(ss => ss.name.toUpperCase() === s.toUpperCase());
                                        if (isSelected) {
                                            setSelectedSuppliers(selectedSuppliers.filter(ss => ss.name.toUpperCase() !== s.toUpperCase()));
                                        } else {
                                            setSelectedSuppliers([
                                                ...selectedSuppliers,
                                                suppliers.find(ss => ss.name.toUpperCase() === s.toUpperCase())
                                            ]);
                                        }
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </>
            )}
            {single && selectedSuppliers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '12px' }}>
                    <Typography variant='body2' component='div'>
                        {selectedSuppliers.map(s => s.name).join(', ')}
                    </Typography>
                    <Button size='small' variant='text' color='primary' sx={{ fontSize: '12px !important', padding: '0px', margin: '0px', textTransform: 'none' }} onClick={() => setSelectedSuppliers([])}>
                        Change
                    </Button>
                </Box>
            )}
            {!single && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '12px', marginLeft: 1 }}>
                    <Typography variant='caption' component='div'>
                        {selectedSuppliers.length} {pluralize('supplier', selectedSuppliers.length)} selected.
                    </Typography>
                    {selectedSuppliers.length > 0 && (
                        <Button size='small' variant='text' color='primary' sx={{ fontSize: '12px !important', padding: '0px 12px', margin: '0px', textTransform: 'none' }} onClick={() => setSelectedSuppliers([])}>
                            Clear
                        </Button>
                    )}
                </Box>
            )}
        </>
    );
}

export default SidePaneRfqsSupplierSelection;

function Option(props) {
    const { option, single, selectedSuppliers, matchedUnmatched, visibleByCategory, supplierNameToSupplier, onClick } = props;
    if (option.startsWith('TITLE-')) {
        const _suppliers = visibleByCategory[matchedUnmatched[option]];
        if (_.isEmpty(_suppliers)) return null;

        const title = {
            'TITLE-Matched-By-Route-Source-And-Destination': 'Routes found',
            'TITLE-Matched-By-Route-Source': 'Route source matched',
            'TITLE-Matched-By-Route-Destination': 'Route destination matched',
            'TITLE-Matched-By-Address-Source-And-Destination': 'Address matched',
            'TITLE-Matched-By-Address-Source': 'Address source matched',
            'TITLE-Matched-By-Address-Destination': 'Address destination matched',
            'TITLE-Unmatched': 'Other suppliers',
        }[option];
        const every = _suppliers?.every(a => selectedSuppliers.find(s => s.name.toUpperCase() === a.toUpperCase()));
        const some = _suppliers?.some(a => selectedSuppliers.find(s => s.name.toUpperCase() === a.toUpperCase()));

        return (
            <div style={{ padding: '0px', margin: '0px', display: 'flex', alignItems: 'center' }}>
                {!single && (
                    <Checkbox
                        color='primary'
                        checked={every}
                        indeterminate={!every && some}
                        indeterminateIcon={<FontAwesomeIcon icon={faSquareMinus} />}
                        checkedIcon={<FontAwesomeIcon icon={faSquareCheck} />}
                        icon={<FontAwesomeIcon icon={faSquare} />}
                        size='small'
                        onClick={onClick}
                    />
                )}
                <Typography variant='caption' component='span' color='primary' sx={single ? { marginLeft: '12px' } : {}}>
                    {title}
                </Typography>
            </div>
        );
    }

    const tags = supplierNameToSupplier[option.toUpperCase()]?.tags;

    return (
        <div style={{ padding: '0px', margin: '0px 0px 0px 12px', display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={onClick}>
            {!single && (
                <Checkbox
                    checkedIcon={<FontAwesomeIcon icon={faSquareCheck} />}
                    icon={<FontAwesomeIcon icon={faSquare} />}
                    checked={!!selectedSuppliers.find(s => s.name.toUpperCase() === option.toUpperCase())}
                    onClick={onClick}
                />
            )}
            <Box sx={{ 
                display: 'flex', 
                gap: '3px', 
                flexDirection: 'column', 
                flex: 1,
                ...(single ? { paddingLeft: '8px' } : {}),
                ...(single && selectedSuppliers.find(s => s.name.toUpperCase() === option.toUpperCase()) ? { backgroundColor: grey[100], color: 'primary.main' } : {})
            }}>
                <Box>
                    {option}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    {tags?.map(t => (
                        <Box sx={{ backgroundColor: grey[400], color: 'white', padding: '2px 6px', borderRadius: '24px', fontSize: '10px' }}>
                            {t.label}
                        </Box>
                    ))}
                </Box>
            </Box>
        </div>
    );
}
